/*
 * Copyright Red Hat, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useApi } from '@backstage/core-plugin-api';

import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { lightspeedApiRef } from '../api/api';
import { ConversationList } from '../types';

// Fetch all conversations
export const useConversations = (): UseQueryResult<ConversationList, Error> => {
  const lightspeedApi = useApi(lightspeedApiRef);
  return useQuery({
    queryKey: ['conversations'],
    queryFn: async () => {
      const response = await lightspeedApi.getConversations();
      return response;
    },
    refetchInterval: query => {
      const data = query.state.data;
      if (!data?.length) return false;
      const hasNullSummary = data.some(c => !c.topic_summary);
      return hasNullSummary ? 2000 : false;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};
